import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Check, Sparkles, BarChart3 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo64MetricasFuncionales() {
    const [accion, setAccion] = useState('');
    const [tipoMetrica, setTipoMetrica] = useState('');
    const [metaConsistencia, setMetaConsistencia] = useState('');
    const [indicadorProgreso, setIndicadorProgreso] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [claridad, setClaridad] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/metricas_funcionales`, { withCredentials: true });
                if (data.data) {
                    if (data.data.accion) setAccion(data.data.accion);
                    if (data.data.tipoMetrica) setTipoMetrica(data.data.tipoMetrica);
                    if (data.data.metaConsistencia) setMetaConsistencia(data.data.metaConsistencia);
                    if (data.data.indicadorProgreso) setIndicadorProgreso(data.data.indicadorProgreso);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'metricas_funcionales', data: { accion, tipoMetrica, metaConsistencia, indicadorProgreso } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [accion, tipoMetrica, metaConsistencia, indicadorProgreso]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [accion, tipoMetrica, metaConsistencia, indicadorProgreso, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (accion.trim().length > 0 && tipoMetrica && metaConsistencia.trim().length > 0 && indicadorProgreso.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/metricas-funcionales/submit`, {
                accion, tipo_metrica: tipoMetrica, meta_consistencia: metaConsistencia, indicador_progreso: indicadorProgreso
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setClaridad(data.claridad);
            setSubmitted(true);
            toast.success('Métricas funcionales definidas');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><BarChart3 className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="metricas-funcionales-success-title">Métricas definidas</h2>
                    <p className="text-[#3E5F6E] font-medium mb-4">Tu sistema de medición está listo</p>
                    {claridad && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Claridad: {claridad}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#3E5F6E] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#3E5F6E] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="metricas_funcionales" chapter={6} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-metricas-funcionales">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Cap. 6</span><span className="text-xs text-[#7A8A93]">6.4</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Métricas funcionales</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Define métricas simples</CardTitle><p className="text-xs sm:text-sm text-white/70">Mide lo que puedes controlar directamente</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">¿Qué acción vas a medir?</Label>
                            <Textarea value={accion} onChange={e => setAccion(e.target.value)} placeholder="Ej: Escribir el informe trimestral..." className="border-[#DCE3E8] rounded-xl min-h-[60px]" data-testid="input-accion-metrica" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Tipo de métrica</Label>
                            <Select defaultValue={tipoMetrica || undefined} onValueChange={setTipoMetrica}>
                                <SelectTrigger className="border-[#DCE3E8] rounded-xl" data-testid="select-tipo-metrica"><SelectValue placeholder="Selecciona el tipo" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="frecuencia">Frecuencia: ¿cuántas veces lo hago?</SelectItem>
                                    <SelectItem value="duracion">Duración: ¿cuánto tiempo dedico?</SelectItem>
                                    <SelectItem value="cumplimiento">Cumplimiento: ¿lo hice o no?</SelectItem>
                                    <SelectItem value="calidad">Calidad: ¿cómo lo hice?</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D]">Meta de consistencia</Label>
                            <p className="text-xs text-[#7A8A93]">¿Cuál es tu objetivo realista y sostenible?</p>
                            <Textarea value={metaConsistencia} onChange={e => setMetaConsistencia(e.target.value)} placeholder="Ej: Escribir al menos 4 de 5 días laborales..." className="border-[#5FAF9D]/20 bg-white rounded-xl min-h-[60px]" data-testid="input-meta-consistencia" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Indicador de progreso</Label>
                            <p className="text-xs text-[#7A8A93]">¿Cómo sabrás que estás avanzando?</p>
                            <Textarea value={indicadorProgreso} onChange={e => setIndicadorProgreso(e.target.value)} placeholder="Ej: Si mantengo 4/5 días durante 3 semanas, estoy progresando..." className="border-[#DCE3E8] rounded-xl min-h-[60px]" data-testid="input-indicador-progreso" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-metricas-funcionales">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Definir métricas</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
